import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Kicker, Reveal } from "@/components/Reveal";
import { ArtworkCard } from "@/components/ArtworkTile";
import CtaBand from "@/components/CtaBand";
import { useSiteData } from "@/context/SiteData";
import { startCheckout, formatPrice, apiErr } from "@/lib/api";
import { usePageMeta } from "@/lib/meta";

export default function Portfolio() {
  usePageMeta(
    "Portfolio | Diamond's Divine Arts",
    "Explore the gallery of Diamond's Divine Arts — biblical art, faith-inspired originals, commissions, and fine art prints by Natalia."
  );

  const { artworks } = useSiteData();
  const [active, setActive] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [params, setParams] = useSearchParams();
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    setImgIdx(0);
  }, [active]);

  useEffect(() => {
    const id = params.get("piece");
    if (id && artworks.length) {
      const art = artworks.find((a) => String(a.id) === id);
      if (art) setActive(art);
    }
  }, [params, artworks]);

  const close = () => {
    setActive(null);
    if (params.get("piece")) setParams({});
  };

  const single = useMemo(() => artworks.length <= 1, [artworks]);

  const buy = async () => {
    setBuying(true);
    try {
      const { checkout_url } = await startCheckout({ item_type: "artwork", item_id: active.id });
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(apiErr(e, "Could not start checkout"));
      setBuying(false);
    }
  };

  return (
    <>
      <section className="divine-rays relative overflow-hidden pb-16 pt-16 md:pb-20 md:pt-24" data-testid="portfolio-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Kicker>Portfolio</Kicker>
            <h1 className="mt-6 font-serif text-4xl font-light leading-[1.08] tracking-tight text-[#1C1815] sm:text-5xl lg:text-6xl">
              The <span className="italic text-[#9A7A3F]">Gallery</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-[#4A423A]">
              Originals, commissions, and prints — each piece created to bring the Word to life.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 pt-4 md:pb-28" data-testid="portfolio-gallery-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className={`grid gap-8 ${single ? "mx-auto max-w-md" : "sm:grid-cols-2 lg:grid-cols-3"}`} data-testid="portfolio-grid">
            {artworks.map((art, i) => (
              <ArtworkCard key={art.id} art={art} index={i} onView={setActive} />
            ))}
          </div>
          {artworks.length === 0 && (
            <p className="py-16 text-center text-sm text-[#8C7E70]">The gallery is being updated — please check back soon.</p>
          )}

          <Reveal className="mt-14 text-center">
            <p className="text-sm font-light text-[#8C7E70]">
              Don't see your piece? It may not exist yet — let's create it together.
            </p>
            <Link to="/booking" className="btn-primary mt-6" data-testid="portfolio-custom-button">
              Request a Custom Piece <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <Dialog open={!!active} onOpenChange={(o) => !o && close()}>
        <DialogContent
          className="max-h-[92vh] max-w-4xl overflow-y-auto border-[#D4AF37]/40 bg-[#FAF7F2] p-0"
          data-testid="artwork-detail-modal"
        >
          {active && (
            <div className="grid md:grid-cols-2">
              <div className="flex flex-col gap-3 bg-[#171310] p-3">
                {(active.images && active.images.length ? active.images : []).length > 0 && (
                  <img
                    src={active.images[imgIdx] || active.images[0]}
                    alt={`${active.title} — photo ${imgIdx + 1}`}
                    className="w-full rounded-sm object-cover"
                    data-testid="artwork-photo-main"
                  />
                )}
                {active.images && active.images.length > 1 && (
                  <div className="flex flex-wrap gap-2" data-testid="artwork-thumbnails">
                    {active.images.map((src, i) => (
                      <button
                        key={src + i}
                        type="button"
                        onClick={() => setImgIdx(i)}
                        className={`h-14 w-14 overflow-hidden rounded-sm border-2 transition ${i === imgIdx ? "border-[#D4AF37]" : "border-transparent opacity-70 hover:opacity-100"}`}
                        data-testid={`artwork-thumb-${i}`}
                      >
                        <img src={src} alt={`thumbnail ${i + 1}`} className="h-full w-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-col p-8 sm:p-10">
                <DialogHeader>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#B89759]">
                    {active.category}
                  </span>
                  <DialogTitle className="mt-2 font-serif text-3xl font-normal text-[#1C1815]">
                    {active.title}
                  </DialogTitle>
                  <DialogDescription className="sr-only">Details for {active.title}</DialogDescription>
                </DialogHeader>
                <p className="mt-5 text-sm font-light leading-relaxed text-[#4A423A]">{active.description}</p>
                <dl className="mt-6 space-y-3 border-t border-[#D4AF37]/30 pt-6 text-sm">
                  {active.medium && (
                    <div className="flex justify-between gap-4">
                      <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8C7E70]">Medium</dt>
                      <dd className="text-right text-[#1C1815]">{active.medium}</dd>
                    </div>
                  )}
                  {(active.size || active.dimensions) && (
                    <div className="flex justify-between gap-4">
                      <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8C7E70]">Size</dt>
                      <dd className="text-right text-[#1C1815]">{active.size || active.dimensions}</dd>
                    </div>
                  )}
                  {active.year && (
                    <div className="flex justify-between gap-4">
                      <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8C7E70]">Year</dt>
                      <dd className="text-right text-[#1C1815]">{active.year}</dd>
                    </div>
                  )}
                  <div className="flex justify-between gap-4">
                    <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8C7E70]">Price</dt>
                    <dd className="text-right font-serif text-xl text-[#9A7A3F]">{formatPrice(active.price)}</dd>
                  </div>
                </dl>
                <p className="mt-5 font-mono text-[9px] uppercase leading-relaxed tracking-[0.2em] text-[#8C7E70]">
                  Original artwork by Natalia — photographed in natural light.
                </p>
                <div className="mt-auto space-y-3 pt-8">
                  {active.price ? (
                    <button onClick={buy} disabled={buying} className="btn-gold w-full disabled:opacity-60" data-testid="artwork-purchase-button">
                      {buying ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      {buying ? "Starting checkout…" : `Buy Now — ${formatPrice(active.price)}`}
                    </button>
                  ) : null}
                  <Link
                    to="/booking"
                    className="btn-primary w-full"
                    data-testid="artwork-inquire-button"
                    onClick={close}
                  >
                    Inquire About This Piece <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <CtaBand
        title="Commission a Piece of Your Own."
        copy="Share your scripture, your story, or your space — and Natalia will bring it to life on canvas."
      />
    </>
  );
}
